import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  Tag,
  ChevronDown,
  Users,
  Folder,
  FileText,
  BadgePercent,
  BadgeDollarSign,
  Boxes,
  Box,
  SquareCode,
  ChevronUp,
} from "lucide-react";
import clsx from "clsx";
import LabelsSelect from "../../../../components/LabelsSelect";
import TaskAction from "../../../../actions/TaskAction";
import BucketAction from "../../../../actions/BucketAction";
import EditableDetailRow from "./../EditableDetailRow";
import AutoTextArea from "./../AutoTextArea";
import Avatar from "../../../../components/Avatar";
import CustomField from "../../../../main/CustomField";
import { FileCode } from "lucide-react";
import InputInline from "../../../../components/InputInline";
import AssigneeSelectV2 from "../../../../components/AssigneeSelect";
import DetailPanelSkeleton from "./DetailPanelSkeleton";
import BucketSelect from "../../../../components/BucketSelect";
import AmountInputField from "./../AmountInputField";
import { formatAmount } from "../../../../api/DataTypeUtils";
import { checkExistProductCode } from "../../../../dialog/ItemDialog";
import LayoutAction from "../../../../actions/LayoutAction";
import Card from "../../../../components/ui/Card";

interface ProductDetailPanelProps {
  projectId: string;
  contentType: string; // expect contentType
}

const ProductDetail = ({ projectId, contentType, isLoading, task, visibleRowNumber }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType)) || fromJS([]);
  const members = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || fromJS([])
  );
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";

  // Ensure buckets are loaded
  useEffect(() => {
    if (projectId && contentType && (!buckets || buckets.size === 0)) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
    }
  }, [dispatch, projectId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTaskFieldValue = (fieldId: string) => task.getIn(["data", fieldId, "value"]);

  // Assignee display names
  const assigneeIds: string[] = (() => {
    const toIdArr = (v: any): string[] => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
      if (typeof v?.toJS === "function") {
        const x = v.toJS();
        if (Array.isArray(x)) return x.map((s: any) => String(s)).filter(Boolean);
        if (typeof x === "string")
          return x
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean);
        return [];
      }
      if (typeof v === "string")
        return v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [];
    };
    return toIdArr(getTaskFieldValue("assignees")).map((s) => String(s));
  })();
  const assigneeMatches = assigneeIds.map((id) => {
    const mByAccount = members.find((m: any) => String(m.get("accountId")) === String(id));
    const mById = mByAccount || members.find((m: any) => String(m.get("id")) === String(id));
    const mByValue = mById || members.find((m: any) => String(m.get("value")) === String(id));
    const found = mByValue || null;
    return {
      accountId: String(id),
      displayName:
        found?.get?.("displayName") || found?.get?.("name") || found?.get?.("email") || String(id),
      email: found?.get?.("email"),
      avatarUrl: found?.get?.("avatarUrl"),
    };
  });

  // Render assignees with avatars
  const renderAssigneeDisplay = () => {
    if (assigneeMatches.length === 0) {
      return <span className="text-gray-500">{t("Unassigned")}</span>;
    }

    const maxVisible = 2;
    const visibleAssignees = assigneeMatches.slice(0, maxVisible);
    const remainingCount = assigneeMatches.length - maxVisible;

    return (
      <div className="flex items-center gap-x-2 overflow-hidden">
        {visibleAssignees.map((user) => (
          <div
            key={`assignee_${user.accountId}`}
            className="flex items-center space-x-1.5 shrink-0"
          >
            <Avatar sizeIcon={4} avatarUrl={user.avatarUrl} displayName={user.displayName} />
            <span className="text-[12px] leading-4 font-medium text-gray-800 whitespace-nowrap">
              {user.displayName}
            </span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="flex items-center space-x-1.5 shrink-0">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-gray-600">+{remainingCount}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Update
  const handleUpdateField = (fieldId: string, fieldValue: any) => {
    const itemId = task.get("id");
    if (itemId) {
      dispatch(TaskAction.updateTask(itemId, fieldId, fieldValue));
    }
  };
  // Render custom fields
  const renderCustomFields = () => {
    if (!fields) return [];

    const customFields = fields.filter(
      (field: any) => field.get("isCustomField") && field.get("formType") === contentType
    );

    if (!customFields || customFields.size === 0) return [];

    return customFields
      .map((customField: any) => {
        const fieldId = customField.get("id");
        const fieldData = {
          ...customField.toJS(),
          value: getTaskFieldValue(fieldId),
        };

        // Format value for display
        const displayValue = () => {
          const val = getTaskFieldValue(fieldId);
          if (val === null || val === undefined || val === "") {
            return <span className="text-gray-400">{t("Set a value...")}</span>;
          }
          if (customField.get("type") === "boolean") {
            return val === "yes" || val === true ? t("Yes") : t("No");
          }
          if (customField.get("type") === "datetime") {
            return val ? new Date(val).toLocaleDateString() : t("Set a value...");
          }
          return String(val);
        };

        return buildRow(
          `custom_${fieldId}`,
          <FileCode className="w-4 h-4 text-gray-500" />,
          t(customField.get("label") || fieldId),
          <div className="w-full">
            <CustomField
              field={fieldData}
              variant="inline"
              onChange={(value: any) => {
                handleUpdateField(fieldId, value);
              }}
            />
          </div>,
          displayValue()
        );
      })
      .toArray();
  };

  // Row builder
  const buildRow = (
    key: string,
    icon: React.ReactNode,
    label: string,
    editComponent: React.ReactNode,
    valueContent: React.ReactNode,
    usePopover?: boolean,
    actions?: ("edit" | "copy" | "show-all")[]
  ) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <EditableDetailRow
        label={label}
        editComponent={editComponent}
        usePopover={usePopover}
        actions={actions}
      >
        {valueContent}
      </EditableDetailRow>
    </div>
  );

  const buildRowLabels = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <LabelsSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getTaskFieldValue("labels")?.toJS() || []}
            onChange={(labelIds: string[]) => handleUpdateField("labels", labelIds)}
            placeholder={t("Select labels")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowBucket = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <BucketSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getTaskFieldValue("bucketId")}
            onChange={(bucketId: string) => handleUpdateField("bucketId", bucketId)}
            placeholder={t("Select...")}
          />
        </div>
      </div>
    </div>
  );

  const name = getTaskFieldValue("name");
  const productCode = getTaskFieldValue("productCode");
  const unit = getTaskFieldValue("unit");
  const unitPrice = getTaskFieldValue("unitPrice") || 0;
  const tax = getTaskFieldValue("tax") || 0;
  const description = getTaskFieldValue("description");

  const rows: React.ReactNode[] = [
    buildRow(
      "name",
      <Boxes className="w-4 h-4 text-gray-500" />,
      t("Product Name"),
      <InputInline
        value={name}
        onFinish={(val: string) => {
          if (val && val.trim()) {
            handleUpdateField("name", val);
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Product name"),
                type: "error",
                message: t("The product name is required"),
              })
            );
          }
        }}
        variant="inline"
      />,
      name ? (
        <span className="line-clamp-2">{name}</span>
      ) : (
        <span className="text-gray-500">{t("Set a value...")}</span>
      )
    ),
    buildRow(
      "productCode",
      <SquareCode className="w-4 h-4 text-gray-500" />,
      t("Product Code"),
      <InputInline
        value={productCode}
        onFinish={async (val: string) => {
          if (val && val.trim()) {
            const isExistProductCode = await checkExistProductCode(projectId, val.trim());
            if (isExistProductCode) {
              dispatch(
                LayoutAction.setNotification({
                  title: t("Product code"),
                  type: "error",
                  message: t("The product code already exists"),
                })
              );
            } else {
              handleUpdateField("productCode", val);
            }
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Product code"),
                type: "error",
                message: t("The product code is required"),
              })
            );
          }
        }}
        variant="inline"
      />,
      productCode ? (
        <span className="line-clamp-2">{productCode}</span>
      ) : (
        <span className="text-gray-500">{t("Set a value...")}</span>
      )
    ),
    // buildRow(
    //   "category",
    //   <ArrowUpFromDot className="w-4 h-4 text-gray-500" />,
    //   t("Status"),
    //   <SelectInline
    //     autoOpen
    //     customTrigger={category}
    //     options={statusOptions}
    //     value={toStr(status)}
    //     onChange={(val: string) => handleUpdateField("category", val)}
    //   />,
    //   category
    // ),
    buildRow(
      "unit",
      <Box className="w-4 h-4 text-gray-500" />,
      t("Unit"),
      <InputInline
        value={unit}
        onFinish={(val: string) => handleUpdateField("unit", val)}
        variant="inline"
      />,
      unit ? (
        <span className="line-clamp-2">{unit}</span>
      ) : (
        <span className="text-gray-500">{t("Set a value...")}</span>
      )
    ),
    buildRow(
      "unitPrice",
      <BadgeDollarSign className="w-4 h-4 text-gray-500" />,
      t("Unit Price"),
      <AmountInputField
        value={unitPrice}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("unitPrice", val)}
      />,
      formatAmount(i18n.language, unitPrice, currency, "0$")
    ),
    buildRow(
      "tax",
      <BadgePercent className="w-4 h-4 text-gray-500" />,
      t("Tax %"),
      <AmountInputField
        value={tax}
        isPercentage
        variant="inline"
        onFinish={(val: string) => handleUpdateField("tax", val)}
      />,
      `${Math.round(tax)}%`
    ),
    buildRow(
      "description",
      <FileText className="w-4 h-4 text-gray-500" />,
      t("Description"),
      <div className="w-full rounded-md min-h-[60px]">
        <AutoTextArea
          rows={3}
          placeholder={t("Add a description...")}
          value={description}
          onChange={(val: string) => handleUpdateField("description", val)}
          className="text-[12px] border-0 leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none resize-none"
          autoFocus={true}
        />
      </div>,
      description ? (
        <span className="line-clamp-2">{description}</span>
      ) : (
        <span className="text-gray-500">{t("Set a value...")}</span>
      ),
      true,
      description ? ["show-all"] : undefined
    ),
    buildRow(
      "assignees",
      <Users className="w-4 h-4 text-gray-500" />,
      t("Owners"),
      <AssigneeSelectV2
        value={getTaskFieldValue("assignees")}
        autoOpen
        showTriggerHeight={true}
        customTrigger={renderAssigneeDisplay()}
        className="z-50"
        multiple={true}
        onChange={(val) => {
          const ids = Array.isArray(val)
            ? val
            : String(val || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
          handleUpdateField("assignees", ids);
        }}
      />,
      renderAssigneeDisplay()
    ),
    buildRowLabels("labels", <Tag className="w-4 h-4 text-gray-500" />, t("Labels")),
    buildRowBucket("bucketId", <Folder className="w-4 h-4 text-gray-500" />, t("Group")),
    // Custom Fields
    ...renderCustomFields(),
  ];

  if (!task.get("id")) {
    return (
      <div className="px-3">
        <div className="text-xs text-gray-500 py-4 text-center">{t("No information")}</div>
      </div>
    );
  }

  const visibleRows = visibleRowNumber === -1 ? rows : rows.slice(0, visibleRowNumber);

  return (
    <div className="px-3">
      {isLoading ? (
        <DetailPanelSkeleton rows={visibleRows.length} />
      ) : (
        visibleRows.map((row, idx) => <React.Fragment key={idx}>{row}</React.Fragment>)
      )}
    </div>
  );
};

const ProductDetailPanel = ({ projectId, contentType }: ProductDetailPanelProps) => {
  const { t } = useTranslation();

  const isLoadingTask = useSelector((state: any) => state.taskReducer.get("pending"));
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["task", contentType])) || fromJS({});

  const [collapsed, setCollapsed] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  const LeftIcon = useMemo(() => {
    return (
      <ChevronDown
        className={clsx(
          "w-4 h-4 transition-transform shrink-0",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    );
  }, [collapsed]);

  if (contentType !== "PRODUCT") return null;

  return (
    <Card
      bgClass={"bg-white"}
      borderClass={"border border-gray-200"}
      roundedClass={"rounded-[12px]"}
      shadow={"custom"}
      shadowClass={"shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]"}
    >
      <div className="px-3 py-2">
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-end gap-1 text-sm leading-5 font-medium text-gray-800 hover:text-gray-700"
        >
          {LeftIcon}
          {<span className="truncate">{t("Product Detail")}</span>}
        </button>
      </div>
      {!collapsed && (
        <>
          <ProductDetail
            projectId={projectId}
            contentType={contentType}
            isLoading={isLoadingTask}
            task={task}
            visibleRowNumber={internalCollapsed ? 6 : -1}
          />
          <div className="px-3 pb-3 mt-2">
            <button
              type="button"
              onClick={() => setInternalCollapsed((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] leading-4 text-gray-500 hover:text-gray-700"
            >
              <span>{internalCollapsed ? t("Expand") : t("Collapse")}</span>
              {internalCollapsed ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronUp className="w-3 h-3" />
              )}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default ProductDetailPanel;
